'use client';

import { useState, useEffect, useRef } from 'react';
import { useAuth } from './auth-provider';

export function PwaInstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [browserInfo, setBrowserInfo] = useState<{
    name: string;
    isIOS: boolean;
    isChrome: boolean;
    isSafari: boolean;
  }>({
    name: '',
    isIOS: false,
    isChrome: false,
    isSafari: false,
  });
  const { isAuthenticated } = useAuth();
  const promptShownRef = useRef(false);
  
  // Handle mounting to avoid hydration issues - only run once
  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);
  
  // Separate useEffect for browser detection - only run once after mount
  useEffect(() => {
    if (!isMounted) return;
    
    // Detect browser info once after mount
    try {
      const ua = navigator.userAgent;
      const isIOS = /iPhone|iPad|iPod/i.test(ua);
      const isChrome = /Chrome/i.test(ua) && !/Edge/i.test(ua);
      const isSafari = /Safari/i.test(ua) && !/Chrome/i.test(ua);
      
      setBrowserInfo({
        name: isIOS ? (isSafari ? 'Safari' : 'iOS Browser') : 
              isChrome ? 'Chrome' : 'Browser',
        isIOS,
        isChrome,
        isSafari,
      });
    } catch (error) {
      console.error('Error detecting browser info:', error);
    }
  }, [isMounted]);
  
  // Handle showing/hiding the prompt based on conditions
  useEffect(() => {
    // Only run on client side and after component mount
    if (!isMounted || promptShownRef.current) return;
    
    try {
      // Check if user already dismissed the prompt
      const hasPromptBeenShown = localStorage.getItem('pwa-install-prompt-shown') === 'true';
      if (hasPromptBeenShown) return;
      
      // Function to detect if the app is running in standalone mode (already installed)
      const isInStandaloneMode = () => 
        window.matchMedia('(display-mode: standalone)').matches || 
        (window.navigator as any).standalone === true;
      
      // Function to detect if the device is mobile
      const isMobileDevice = () => {
        return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      };
      
      // Only show prompt if:
      // 1. User is authenticated
      // 2. App is not already in standalone mode
      // 3. User is on mobile device
      // 4. Prompt hasn't been shown before
      if (isAuthenticated && !isInStandaloneMode() && isMobileDevice() && !hasPromptBeenShown) {
        // Slight delay to not show immediately on page load
        const timer = setTimeout(() => {
          setShowPrompt(true);
          promptShownRef.current = true;
        }, 2000);
        
        return () => clearTimeout(timer);
      }
    } catch (error) {
      console.error('Error in PWA install prompt:', error);
    }
  }, [isAuthenticated, isMounted]);
  
  const dismissPrompt = () => {
    try {
      setShowPrompt(false);
      localStorage.setItem('pwa-install-prompt-shown', 'true');
    } catch (error) {
      console.error('Error storing PWA prompt preference:', error);
    }
  };
  
  // Don't render anything on server or if prompt shouldn't be shown
  if (!isMounted || !showPrompt) return null;
  
  return (
    <div className="fixed bottom-4 left-4 right-4 glassmorphic rounded-xl p-6 shadow-glass-lg z-50 animate-in slide-in-from-bottom duration-200">
      <h3 className="font-semibold text-lg mb-2">Install DateHack App</h3>
      <p className="text-sm text-muted-foreground mb-3">
        Add DateHack to your home screen for a better experience!
      </p>
      
      {browserInfo.isIOS && browserInfo.isSafari && (
        <p className="text-xs text-muted-foreground mb-3">
          Tap the share icon <span className="inline-block">⎅</span> at the bottom of your screen, 
          then select "Add to Home Screen"
        </p>
      )}
      
      {browserInfo.isChrome && !browserInfo.isIOS && (
        <p className="text-xs text-muted-foreground mb-3">
          Tap the menu icon ⋮ and select "Add to Home screen"
        </p>
      )}
      
      <div className="flex justify-end gap-3">
        <button
          onClick={dismissPrompt}
          className="px-4 py-2 text-sm glassmorphic-light hover:glassmorphic rounded-lg transition-all duration-200"
        >
          Not now
        </button>
        <button
          onClick={dismissPrompt}
          className="px-4 py-2 text-sm bg-primary/90 hover:bg-primary text-primary-foreground rounded-lg transition-all duration-200"
        >
          Got it
        </button>
      </div>
    </div>
  );
} 